import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Flame } from "lucide-react";

const HERO_FLATLAY =
  "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/c1owqc1u_Cotton%20Fleece%20hoodies.jpg";

const tiles = [
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/27zw8r87_Cotton%20Fleece%20hoodies%20%282%29.jpg",
    name: "Jet Black",
    swatch: "#0A0A0A",
    fg: "#FFFFFF",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/7pt6tpt6_Cotton%20Fleece%20hoodies%20%283%29.jpg",
    name: "Cocoa Brown",
    swatch: "#5C3A2E",
    fg: "#FFFFFF",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/p1t9vzlk_Cotton%20Fleece%20hoodies%20%284%29.jpg",
    name: "Navy",
    swatch: "#1E2A4A",
    fg: "#FFFFFF",
  },
];

const fabrics = [
  {
    name: "Pure Cotton Fleece",
    blurb: "Premium 320 GSM brushed-back cotton. Heavy, soft, holds shape wash after wash.",
    bg: "#FF0055",
    fg: "#FFFFFF",
    tag: "Premium",
  },
  {
    name: "Polycotton Fleece",
    blurb: "Cotton-poly blend at a friendlier price. Same warmth, lighter on the wallet.",
    bg: "#00F0FF",
    fg: "#0A0A0A",
    tag: "Budget",
  },
];

const specs = [
  "320 GSM Fleece",
  "Drawstring Hood",
  "Kangaroo Pocket",
  "Ribbed Cuffs",
  "Sizes S – XXL",
  "Print + Embroidery",
];

export default function Hoodies() {
  return (
    <section
      id="hoodies"
      className="border-b-4 border-black py-20 lg:py-28 bg-[#FDFDFD]"
      data-testid="hoodies-section"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
        {/* Header */}
        <div className="grid grid-cols-12 gap-8 mb-12 lg:mb-16">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#555] mb-4">
              /// Hoodies · Fleece line
            </div>
            <h2 className="font-heading uppercase text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Pull up.
              <br />
              <span className="bg-[#FFE600] nb-border-2 px-2 inline-block">Hoodie season,</span>{" "}
              every season.
            </h2>
            <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-[#0A0A0A]/80">
              Heavy 320 GSM brushed-back fleece pullovers built for streetwear
              drops, college merchandise and brand launches. Two fabric tiers —
              pure cotton (premium) or polycotton blend (budget). Customisable
              with embroidery, puff print, DTF or screen-print on chest, back
              or sleeves.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-5 flex flex-wrap items-end gap-2 content-end">
            {specs.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1.5 nb-border-2 bg-white px-3 py-1.5 font-heading uppercase text-xs tracking-wider"
                data-testid={`hoodie-spec-${s.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Check size={12} strokeWidth={3} className="text-[#FF0055]" />
                {s}
              </span>
            ))}
          </div>
        </div>

        {/* Fabric option cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6 mb-10">
          {fabrics.map((f, i) => (
            <motion.div
              key={f.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="relative nb-border-4 nb-shadow p-6 md:p-8"
              style={{ background: f.bg, color: f.fg }}
              data-testid={`hoodie-fabric-${i}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div
                    className="inline-block nb-border-2 px-2 py-0.5 font-heading uppercase text-[10px] tracking-widest mb-3"
                    style={{ background: f.fg, color: f.bg }}
                  >
                    {f.tag}
                  </div>
                  <h3 className="font-heading uppercase text-2xl md:text-3xl leading-tight">
                    {f.name}
                  </h3>
                  <p className="mt-3 text-sm md:text-base leading-relaxed opacity-95 max-w-md">
                    {f.blurb}
                  </p>
                </div>
                <Flame size={36} strokeWidth={2.5} className="flex-shrink-0 opacity-90" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Hero flatlay */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="relative nb-border-4 nb-shadow-lg overflow-hidden bg-white aspect-[16/9] md:aspect-[21/9]"
          data-testid="hoodies-flatlay"
        >
          <img
            src={HERO_FLATLAY}
            alt="Cotton fleece hoodies in black, cocoa brown and navy"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute top-4 left-4 nb-border-2 bg-black text-[#FFE600] px-3 py-1.5 font-heading uppercase text-xs tracking-wider">
            Three hoods, one mood
          </div>
        </motion.div>

        {/* Color detail tiles */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {tiles.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="nb-tile nb-border-4 nb-shadow-sm bg-white aspect-[3/4]"
              data-testid={`hoodie-tile-${i}`}
            >
              <img
                src={t.url}
                alt={`Cotton fleece hoodie in ${t.name}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              <div
                className="absolute bottom-3 left-3 right-3 nb-border-2 px-3 py-2 font-heading uppercase text-xs tracking-wider"
                style={{ background: t.swatch, color: t.fg }}
              >
                {t.name}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 nb-border-4 bg-[#FFE600] p-5 md:p-7">
          <div>
            <div className="font-heading uppercase text-xs tracking-[0.3em] text-[#FF0055] mb-1">
              Drop &amp; college merch
            </div>
            <div className="font-heading uppercase text-xl md:text-2xl leading-tight">
              MOQ 30 pcs · Mix fabrics, mix sizes, mix designs.
            </div>
          </div>
          <a href="#contact" className="nb-btn nb-btn-magenta" data-testid="hoodies-cta">
            Quote my hoodies <ArrowRight size={18} strokeWidth={3} />
          </a>
        </div>
      </div>
    </section>
  );
}
